import { useMemo } from 'react';
import {
  EventInput,
  EventStatus,
  GetShiftsByEventCommandOutput,
  ShiftInput,
} from '@amzn/red-velvet-api';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useAllPagesRedVelvetQuery } from '../allPagesRedVelvetQuery';
import { useFeatures, getEnabledFeatures } from '../features';
import { getClient } from '../../utils/getClient';
import { mapEventToEventInput, mapShiftToShiftInput } from '../../utils/eventUtils';
import { getHttpStatusCode } from '../../utils/errorHelper';
import { getAliasFromEmail } from '../../utils/contactUtils';

interface UseEventDraftPrePopulationResult {
  eventInput: EventInput | null;
  shiftInputs: ShiftInput[];
  ownerAlias: string | undefined;
  eventStatus: EventStatus | undefined;
  isLoading: boolean;
  error: string | null;
  isNotFound: boolean;
}

export function useEventDraftPrePopulation(
  draftId: string | null
): UseEventDraftPrePopulationResult {
  const enabled = !!draftId;
  const id = draftId ?? '';
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const client = getClient(getEnabledFeatures(features, searchParams));

  const {
    data: eventData,
    isLoading: isEventLoading,
    error: eventError,
  } = useQuery({
    enabled,
    queryKey: ['RedVelvetApi', 'getEventDraftByEventId', { eventId: id }],
    queryFn: () => client.getEventDraftByEventId({ eventId: id }),
  });

  const {
    data: shifts,
    isPending: isShiftsPending,
    error: shiftsError,
  } = useAllPagesRedVelvetQuery({
    enabled,
    redVelvetQueryKey: ['getShiftsByEvent', { eventId: id }],
    converter: (output: GetShiftsByEventCommandOutput) => ({
      items: output.shifts ?? [],
      next: output.page,
    }),
  });

  const eventInput = useMemo(
    () => (eventData ? mapEventToEventInput(eventData) : null),
    [eventData]
  );

  const shiftInputs = useMemo(
    () => (shifts ?? []).map((shift) => mapShiftToShiftInput(shift)),
    [shifts]
  );

  const httpStatus = getHttpStatusCode(eventError);
  const isNotFound =
    eventError != null &&
    (httpStatus === 404 ||
      httpStatus === 409 ||
      eventError.name === 'ResourceNotFoundException' ||
      eventError.name === 'ConflictException');

  return {
    eventInput,
    shiftInputs,
    ownerAlias: getAliasFromEmail(eventData?.ownerEmail),
    eventStatus: eventData?.status,
    isLoading: isEventLoading || isShiftsPending,
    error:
      eventError?.message ||
      shiftsError?.message ||
      (eventError || shiftsError ? 'An error occurred' : null),
    isNotFound,
  };
}
